import React, { useState, useEffect, useRef, useCallback } from "react"
import { navigate } from "vike/client/router"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  ArrowLeftIcon,
  ClockIcon,
  TagIcon,
  PencilIcon,
  TrashIcon,
  ShieldExclamationIcon,
  ArrowPathIcon, // Used for merge icon
} from "@heroicons/react/24/solid"
import moment from "moment"
import {
  GetTicketDetails,
  SendMessage,
  UpdateTicket,
  DeleteTickets,
  GetStatistics,
  GetMessages,
} from "./api"
import AssignDialog from "./AssignDialog"
import ChatBubble from "./ChatBubble"
import ChatArea from "./ChatArea"
import TagDialog from "./TagDialog"
import SupportNoteDialog from "./SupportNoteDialog"
import MergeTickets from "./MergeTickets"
import MergeInfo from "./MergeInfo"
import Value from "aldoo-ra/Value"
import Tooltip from "aldoo-ra/Tooltip"
import Icon from "aldoo-ra/Icon"
import Panel from "aldoo-ra/Panel"

export default function TicketDetails({ ticketId, onClose }) {
  const [userProfile] = Value("user-profile")
  const [, setStatistics] = Value("cs-statistics", null)
  const [loading, setLoading] = useState(true)
  const [ticketData, setTicketData] = useState(null)
  const [sending, setSending] = useState(false)

  // Create a deduplication utility function
  const deduplicateMessages = (messages) => {
    if (!messages || !Array.isArray(messages)) return []

    const uniqueMessages = new Map()

    // Add each message to the map with ID as key (last one with same ID wins)
    messages.forEach((message) => {
      if (message && message._id) {
        uniqueMessages.set(message._id, message)
      }
    })

    // Convert back to array and sort by timestamp
    return Array.from(uniqueMessages.values()).sort((a, b) => {
      const timeA = new Date(a.timestamp || 0).getTime()
      const timeB = new Date(b.timestamp || 0).getTime()
      return timeA - timeB
    })
  }

  // Create a wrapped version of setTicketData that handles deduplication
  const safeSetTicketData = (updater) => {
    setTicketData((prevData) => {
      // Handle the case where updater is a function
      const newData =
        typeof updater === "function" ? updater(prevData) : updater

      // If no data or no ticket, return as is
      if (!newData || !newData.ticket || !newData.ticket.messages) {
        return newData
      }

      // Deduplicate messages
      const dedupedMessages = deduplicateMessages(newData.ticket.messages)

      return {
        ...newData,
        ticket: {
          ...newData.ticket,
          messages: dedupedMessages,
        },
      }
    })
  }

  // Polling related state and refs
  const pollIntervalRef = useRef(null)
  const POLL_INTERVAL = 10000 // Poll every 10 seconds

  const [assignDialog, setAssignDialog] = useState({
    isOpen: false,
    ticketId: null,
    ticketLabel: "",
    currentAssignee: null,
  })

  const [tagDialog, setTagDialog] = useState({
    isOpen: false,
    ticketId: null,
    ticketLabel: "",
    currentTags: [],
  })

  const [supportNoteDialog, setSupportNoteDialog] = useState({
    isOpen: false,
    ticketId: null,
  })

  // Add state for merge tickets dialog
  const [mergeDialog, setMergeDialog] = useState({
    isOpen: false,
    ticketId: null,
    ticketLabel: "",
  })

  // Add ref for messages container
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  
  // Add refs for width debugging
  const mainContainerRef = useRef(null)
  const flexContainerRef = useRef(null)
  const leftColumnRef = useRef(null)
  const rightColumnRef = useRef(null)
  const headerRef = useRef(null)

  // User details configuration
  const userDetailsConfig = [
    {
      label: "User Value",
      key: "userValue",
      fallback: 18,
      format: (val) => `$${val}`,
    },
    { label: "Name", key: "name", fallback: "Aldoo User" },
    { label: "Email", key: "email", fallback: "adrianaf1611@gmail.com" },
    { label: "Account Type", key: "accountType", fallback: "premium" },
    { label: "Credits", key: "credits", fallback: 50 },
    {
      label: "Created",
      key: "createdAt",
      fallback: "2025-05-06T14:08:57.748Z",
      format: (date) => new Date(date).toLocaleDateString(),
    },
    {
      label: "Account Age",
      key: "accountAge",
      fallback: 0,
      format: (days) => `${days} days`,
    },
    {
      label: "Platforms",
      key: "platforms",
      fallback: ["Web"],
      format: (platforms) =>
        Array.isArray(platforms) ? platforms[0] : platforms,
    },
    {
      label: "Total Credits Purchased",
      key: "totalCreditsPurchased",
      fallback: 50,
    },
    { label: "Total Credits Spent", key: "totalCreditsSpent", fallback: 0 },
    { label: "Id", key: "id", fallback: "681a17f9fd8e65bea8161109" },
    { label: "Transactions Count", key: "transactionsCount", fallback: 1 },
    { label: "Orders Count", key: "ordersCount", fallback: 0 },
    {
      label: "Stripe Customer Id",
      key: "stripeCustomerId",
      fallback: "cus_SGInUeyQBPCMjH",
    },
  ]

  // Format response time using moment.js
  const formatResponseTime = (seconds) =>
    !seconds && seconds !== 0
      ? "N/A"
      : moment.duration(seconds, "seconds").humanize()

  // Format detailed response time using moment.js
  const formatDetailedResponseTime = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A"

    const duration = moment.duration(seconds, "seconds")
    const days = Math.floor(duration.asDays())
    const hours = duration.hours()
    const minutes = duration.minutes()
    const secs = duration.seconds()

    const parts = []
    if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`)
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`)
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "min" : "mins"}`)
    if (secs > 0 || parts.length === 0)
      parts.push(`${secs} ${secs === 1 ? "sec" : "secs"}`)

    return parts.join(", ")
  }

  // Scroll to bottom with a smooth animation when sending a message, but instant for loading initial messages
  const scrollToBottom = (behavior = "instant") =>
    messagesEndRef.current?.scrollIntoView({
      behavior,
      block: "end",
    })

  // Function to refresh global statistics
  const refreshStatistics = async () => {
    try {
      const response = await GetStatistics()
      if (!response.result || !response.data) return
      setStatistics(response.data)
    } catch (error) {
      console.error("Failed to refresh statistics:", error)
    }
  }

  // Function to get the timestamp of the latest message
  const getLatestMessageTimestamp = useCallback(() => {
    if (
      !ticketData?.ticket?.messages ||
      ticketData.ticket.messages.length === 0
    )
      return null

    // Find the message with the latest timestamp
    const latestTimestamp = ticketData.ticket.messages.reduce(
      (latest, message) => {
        if (!message.timestamp) return latest
        const messageTime = new Date(message.timestamp).getTime()
        return messageTime > latest ? messageTime : latest
      },
      0
    )

    return latestTimestamp > 0 ? latestTimestamp : null
  }, [ticketData])

  // Function to poll for new messages
  const pollForNewMessages = useCallback(async () => {
    if (!ticketId || !ticketData) return

    try {
      const latestTimestamp = getLatestMessageTimestamp()
      if (!latestTimestamp) return

      // Add a small buffer (1ms) to avoid getting the last message again
      const timestampToUse = new Date(latestTimestamp + 1)

      const response = await GetMessages({
        ticketId,
        after: timestampToUse,
      })

      if (!response || !response.result) return

      // If there are new messages
      if (
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        // New messages found, update ticket data
        safeSetTicketData((prevData) => {
          if (!prevData) return prevData

          // Add new messages to the existing messages array
          const combinedMessages = [
            ...prevData.ticket.messages,
            ...response.data,
          ]

          const updatedTicket = {
            ...prevData.ticket,
            messages: combinedMessages,
          }

          return {
            ...prevData,
            ticket: updatedTicket,
          }
        })

        // Scroll to bottom to show new messages - use smooth scrolling for incoming messages
        setTimeout(() => scrollToBottom("smooth"), 100)
      }
    } catch (error) {
      console.error("Error polling for new messages:", error)
    }
  }, [ticketId, ticketData, getLatestMessageTimestamp])

  // Set up polling
  useEffect(() => {
    // Start polling
    const startPolling = () => {
      // Clear any existing interval first to avoid duplicates
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }

      // Set initial poll immediately to check for messages that might have arrived
      // between loading the ticket and starting the polling
      pollForNewMessages().catch((err) =>
        console.error("Error during initial message poll:", err)
      )

      // Then set up the interval for regular polling
      pollIntervalRef.current = setInterval(() => {
        pollForNewMessages().catch((err) =>
          console.error("Error during interval message poll:", err)
        )
      }, POLL_INTERVAL)
    }

    // Stop polling
    const stopPolling = () => {
      if (!pollIntervalRef.current) return

      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }

    // Start polling if ticket data is loaded and component is mounted
    if (ticketId && ticketData && !loading) {
      startPolling()
    } else {
      // Ensure polling is stopped if conditions aren't met
      stopPolling()
    }

    // Cleanup - stop polling when component unmounts or ticket changes
    return () => {
      stopPolling()
    }
  }, [ticketId, ticketData, loading, pollForNewMessages])

  // Modified to accept a showLoading parameter
  const loadTicketDetails = async (showLoading = true) => {
    if (!ticketId) return

    try {
      // Only set loading state if showLoading is true
      if (showLoading) {
        setLoading(true)
      }

      const response = await GetTicketDetails(ticketId)
      if (response.data) {
        if (ticketData) {
          // When we already have data, merge messages
          safeSetTicketData({
            ...response.data,
            ticket: {
              ...response.data.ticket,
              // Include both existing and new messages - deduplication happens in safeSetTicketData
              messages: [
                ...ticketData.ticket.messages,
                ...response.data.ticket.messages,
              ],
            },
          })
        } else {
          // First load - still use safeSetTicketData for consistency
          safeSetTicketData(response.data)
        }

        // Scroll to bottom after loading messages, using instant behavior
        setTimeout(() => scrollToBottom("instant"), 100) // Small delay to ensure DOM is updated
      }
    } catch (error) {
      showNotification({
        message: "Failed to load ticket details",
        className: "bg-admin_error text-white",
      })
      onClose && onClose()
    } finally {
      // Only update loading state if we initially set it to true
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  // Added a new function to just refresh the ticket status without loading indicator
  const refreshTicketStatus = async () => {
    try {
      const response = await GetTicketDetails(ticketId)
      if (response.data && ticketData) {
        // Only update the status, keep existing messages
        safeSetTicketData((prevData) => ({
          ...prevData,
          ticket: {
            ...prevData.ticket,
            status: response.data.ticket.status,
          },
        }))
      }
    } catch (error) {
      console.error("Failed to refresh ticket status:", error)
    }
  }

  useEffect(() => {
    if (ticketId) loadTicketDetails()
  }, [ticketId])

  // Debug width logging
  useEffect(() => {
    const logWidths = () => {
      // Debug logging removed
    }

    // Log on mount and data changes
    if (ticketData) {
      setTimeout(logWidths, 100) // Small delay to ensure DOM is rendered
    }
  }, [ticketData])

  // Scroll to bottom when ticket data changes
  useEffect(() => {
    if (!ticketData) return
    scrollToBottom()
  }, [ticketData])

  // Function to navigate to another ticket
  const navigateToTicket = (targetTicketId) => {
    navigate(`/admin/customer-support/tickets/${targetTicketId}`)
  }

  const handleSendMessage = async ({ body, files }) => {
    if (!body.trim() && (!files || files.length === 0)) return

    setSending(true)

    // Get the author from the user profile
    const author = userProfile?._id || userProfile?.id || userProfile?.email
    if (!author) {
      showNotification({
        message: "User information not available",
        className: "bg-admin_error text-white",
      })
      setSending(false)
      return
    }

    // Create a temporary optimistic message with unique ID
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const tempMessage = {
      _id: tempId,
      body: body,
      author: author,
      authorInfo: {
        name: userProfile?.name,
        email: userProfile?.email,
      },
      messageType: "message",
      timestamp: new Date().toISOString(),
      fileAttachments: files?.map(f => f.name) || [],
      localFiles: files || [], // Store actual File objects for local preview
      isUploading: true,
    }

    // Add the temporary message to the UI immediately
    safeSetTicketData((prevData) => {
      if (!prevData) return prevData

      return {
        ...prevData,
        ticket: {
          ...prevData.ticket,
          messages: [...prevData.ticket.messages, tempMessage],
        },
      }
    })

    // Scroll to bottom to show the new message
    setTimeout(() => scrollToBottom("smooth"), 50)

    try {
      // Send the message to the server
      const response = await SendMessage({
        ticketId,
        body: body,
        author: author,
        messageType: "message",
        files: files,
      })

      // If successful, replace the temporary message with the real one
      if (response && response.result && response.data) {
        safeSetTicketData((prevData) => {
          if (!prevData) return prevData

          // Replace temp message with real message
          const updatedMessages = prevData.ticket.messages.map(msg =>
            msg._id === tempId ? response.data : msg
          )

          return {
            ...prevData,
            ticket: {
              ...prevData.ticket,
              messages: updatedMessages,
            },
          }
        })

        // Refresh statistics in the background
        refreshStatistics()

        // Don't reload ticket details, we already have the message
        // await loadTicketDetails(false)
      } else {
        // Remove temporary message on failure
        safeSetTicketData((prevData) => {
          if (!prevData) return prevData

          return {
            ...prevData,
            ticket: {
              ...prevData.ticket,
              messages: prevData.ticket.messages.filter(msg => msg._id !== tempId),
            },
          }
        })

        showNotification({
          message: response?.message || "Failed to send message",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      // Remove temporary message on error
      safeSetTicketData((prevData) => {
        if (!prevData) return prevData

        return {
          ...prevData,
          ticket: {
            ...prevData.ticket,
            messages: prevData.ticket.messages.filter(msg => msg._id !== tempId),
          },
        }
      })

      showNotification({
        message: error?.response?.data?.message || "Failed to send message",
        className: "bg-admin_error text-white",
      })
    } finally {
      setSending(false)
    }
  }

  // New function to handle send and resolve
  const handleSendAndResolve = async ({ body, files }) => {
    if (!body.trim() && (!files || files.length === 0)) return

    setSending(true)

    // Get the author from the user profile
    const author = userProfile?._id || userProfile?.id || userProfile?.email
    if (!author) {
      showNotification({
        message: "User information not available",
        className: "bg-admin_error text-white",
      })
      setSending(false)
      return
    }

    // Create a temporary optimistic message with unique ID
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const tempMessage = {
      _id: tempId,
      body: body,
      author: author,
      authorInfo: {
        name: userProfile?.name,
        email: userProfile?.email,
      },
      messageType: "message",
      timestamp: new Date().toISOString(),
      fileAttachments: files?.map(f => f.name) || [],
      localFiles: files || [], // Store actual File objects for local preview
      isUploading: true,
    }

    // Add the temporary message to the UI immediately
    safeSetTicketData((prevData) => {
      if (!prevData) return prevData

      return {
        ...prevData,
        ticket: {
          ...prevData.ticket,
          messages: [...prevData.ticket.messages, tempMessage],
        },
      }
    })

    // Scroll to bottom to show the new message
    setTimeout(() => scrollToBottom("smooth"), 50)

    try {
      // Send the message to the server
      const response = await SendMessage({
        ticketId,
        body: body,
        author: author,
        messageType: "message",
        files: files,
      })

      // If successful, replace the temporary message with the real one
      if (response && response.result && response.data) {
        // Then resolve the ticket
        await UpdateTicket({ ticketId, status: "resolved" })

        safeSetTicketData((prevData) => {
          if (!prevData) return prevData

          // Replace temp message with real message and update status
          const updatedMessages = prevData.ticket.messages.map(msg =>
            msg._id === tempId ? response.data : msg
          )

          return {
            ...prevData,
            ticket: {
              ...prevData.ticket,
              messages: updatedMessages,
              status: "resolved",
            },
          }
        })

        // Refresh statistics in the background
        await refreshStatistics()

        showNotification({
          message: "Message sent and ticket resolved successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })
      } else {
        // Remove temporary message on failure
        safeSetTicketData((prevData) => {
          if (!prevData) return prevData

          return {
            ...prevData,
            ticket: {
              ...prevData.ticket,
              messages: prevData.ticket.messages.filter(msg => msg._id !== tempId),
            },
          }
        })

        showNotification({
          message: response?.message || "Failed to send message or resolve ticket",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      // Remove temporary message on error
      safeSetTicketData((prevData) => {
        if (!prevData) return prevData

        return {
          ...prevData,
          ticket: {
            ...prevData.ticket,
            messages: prevData.ticket.messages.filter(msg => msg._id !== tempId),
          },
        }
      })

      showNotification({
        message: error?.response?.data?.message || "Failed to send message or resolve ticket",
        className: "bg-admin_error text-white",
      })
    } finally {
      setSending(false)
    }
  }

  const handleStatusChange = async (newStatus) => {
    try {
      await UpdateTicket({ ticketId, status: newStatus })

      // Get updated ticket details without showing loading indicator
      await loadTicketDetails(false)
      await refreshStatistics()

      showNotification({
        message: `Ticket ${
          newStatus === "resolved" ? "resolved" : "reopened"
        } successfully`,
        className: "bg-admin_primary text-admin_text_inv",
      })
    } catch (error) {
      showNotification({
        message: `Failed to ${
          newStatus === "resolved" ? "resolve" : "reopen"
        } ticket`,
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle toggle spam status
  const handleToggleSpam = async () => {
    try {
      if (!ticketData?.ticket) return

      // Toggle the current isSpam value
      const newIsSpamValue = !(ticketData.ticket.isSpam || false)

      await UpdateTicket({
        ticketId,
        isSpam: newIsSpamValue,
      })

      await refreshStatistics()

      showNotification({
        message: `Ticket ${
          newIsSpamValue ? "marked" : "unmarked"
        } as spam successfully`,
        className: "bg-admin_primary text-admin_text_inv",
      })

      // If we're marking a ticket as spam (not unmarking it),
      // close the ticket details and return to browser
      if (newIsSpamValue) {
        onClose && onClose()
      } else {
        // Only reload ticket details if we're staying on this screen - without loading indicator
        await loadTicketDetails(false)
      }
    } catch (error) {
      showNotification({
        message: "Failed to update spam status",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handler for merging tickets
  const handleOpenMergeDialog = () => {
    if (!ticketData?.ticket) return

    // Check if this ticket is already part of a merge
    if (ticketData.ticket.isMerged) {
      showNotification({
        message:
          "This ticket is already part of a merge and cannot be merged again.",
        className: "bg-admin_error text-white",
      })
      return
    }

    // Open merge dialog
    setMergeDialog({
      isOpen: true,
      ticketId: ticketData.ticket.ticketID,
      ticketLabel: `${ticketData.ticket.ticketID} - ${ticketData.ticket.summary}`,
    })
  }

  // Handle delete ticket
  const handleDeleteTicket = () => {
    if (!ticketData?.ticket) return

    showPromptDialog({
      title: "Delete Ticket",
      message: `Are you sure you want to delete ticket #${ticketData.ticket.ticketID}? This action can be reversed from the Restore function.`,
      confirmText: "Delete",
      confirmVariant: "error_m", // Use error variant for delete confirmation
      onConfirm: async () => {
        try {
          const response = await DeleteTickets(ticketData.ticket.ticketID)
          if (!response.result) {
            showNotification({
              message: response.message || "Failed to delete ticket",
              className: "bg-admin_error text-white",
            })
            return
          }

          await refreshStatistics()
          showNotification({
            message: "Ticket deleted successfully",
            className: "bg-admin_primary text-admin_text_inv",
          })
          onClose && onClose()
        } catch (error) {
          showNotification({
            message: "An error occurred while deleting the ticket",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Typography variant="admin_body">Loading ticket details...</Typography>
      </div>
    )
  }

  if (!ticketData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Typography variant="admin_body">Ticket not found</Typography>
      </div>
    )
  }

  const { ticket, author, mergeInfo } = ticketData

  return (
    <div ref={mainContainerRef} className="h-[100vh] w-full max-w-full bg-admin_canvas p-6">
      <div ref={flexContainerRef} className="grid gap-4 h-full" style={{gridTemplateColumns: 'calc(100% - 384px - 1rem) 384px', gridTemplateRows: '1fr', width: '100%'}}>
        {/* Left Column - Header and Chat */}
        <div ref={leftColumnRef} className="flex flex-col gap-4 min-w-0 overflow-hidden h-full">
          {/* Ticket Header */}
          <div ref={headerRef} className="flex flex-col gap-3 bg-admin_paper border border-admin_bordered rounded flex-none p-4 w-full max-w-full overflow-hidden">
            {/* Ticket Title and Tags */}
            <div className="flex items-center justify-between w-full min-w-0">
              <Tooltip
                text={ticket.summary}
              >
                <Typography
                  component="h1"
                  variant="admin_h4"
                  className="text-admin_text truncate max-w-[66%] overflow-hidden cursor-pointer"
                >
                  {ticket.summary && ticket.summary.length > 100 
                    ? `${ticket.summary.substring(0, 100)}...` 
                    : ticket.summary}
                </Typography>
              </Tooltip>

              {/* Tags */}
              <div className="flex flex-wrap gap-1">
                {ticket.tags && ticket.tags.length > 0 && (
                  <Typography
                    key={0}
                    variant="admin_txt1"
                    className="bg-admin_info_bg text-admin_text_secondary rounded-full px-3 py-1 my-0"
                  >
                    {ticket.tags[0]}
                  </Typography>
                )}

                {ticket.tags && ticket.tags.length > 1 && (
                  <Tooltip
                    text={
                      <div className="p-1">
                        {ticket.tags.slice(1).map((tag, i) => (
                          <div key={i} className="py-1">
                            {tag}
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <Typography
                      variant="admin_txt1"
                      className="bg-admin_primary/10 text-admin_text_secondary rounded-full px-2 py-1 my-0 cursor-pointer"
                    >
                      +{ticket.tags.length - 1}
                    </Typography>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                {/* Status Chip */}
                <span
                  className={`px-2 py-1 rounded text-sm font-medium ${
                    // Only show "Merged" for secondary tickets, not primary tickets
                    mergeInfo?.isSecondary
                      ? "text-admin_text_inv bg-admin_warning"
                      : ticket.status === "open"
                      ? "text-admin_text_inv bg-admin_neutral_3"
                      : ticket.status === "pending"
                      ? "text-admin_text_inv bg-admin_progress"
                      : ticket.status === "resolved"
                      ? "text-admin_text_inv bg-admin_success"
                      : "text-admin_text bg-admin_neutral_1"
                  }`}
                >
                  {mergeInfo?.isSecondary
                    ? "Merged"
                    : ticket.status.charAt(0).toUpperCase() +
                      ticket.status.slice(1)}
                </span>
                
                {/* Ticket Number */}
                <Typography
                  variant="admin_h4"
                  className="text-admin_text font-medium"
                >
                  Ticket #{ticket.ticketID}
                </Typography>
              </div>

              {/* Response Time indicator */}
              {ticket.responseTime !== null &&
                ticket.responseTime !== undefined && (
                  <Tooltip
                    text={`Actual time: ${formatDetailedResponseTime(
                      ticket.responseTime
                    )}`}
                  >
                    <div className="flex items-center gap-1 border border-admin_primary/20 rounded px-2 py-1">
                      <ClockIcon className="w-4 h-4 text-admin_text_secondary" />

                      <Typography 
                        variant="admin_txt1"
                        className="text-admin_text_secondary"
                      >
                        Avg. Response Time:{" "}
                        {formatResponseTime(ticket.responseTime)}
                      </Typography>
                    </div>
                  </Tooltip>
                )}
            </div>
          </div>

          {/* Messages Area */}
          <div
            ref={messagesContainerRef}
            className="bg-admin_paper border border-admin_bordered rounded-md p-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0"
          >
            <div className="space-y-16">
              {ticket.messages.map((message) => {
                const isUserMessage = message.author === ticket.author
                const isNote = message.messageType === "note"

                return (
                  <ChatBubble
                    key={message._id}
                    message={message}
                    type={isNote ? "note" : "message"}
                    variant={isUserMessage ? "user" : "support"}
                    onTicketClick={navigateToTicket}
                  />
                )
              })}
              {/* Invisible element at the end to ensure proper scrolling */}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Chat Input - Disabled for secondary (merged) tickets */}
          {!mergeInfo?.isSecondary ? (
            <div className="flex-none">
              <ChatArea
                onSendMessage={handleSendMessage}
                onSendAndResolve={handleSendAndResolve}
                sending={sending}
              />
            </div>
          ) : (
            <div className="flex-none p-4 border-2 border-admin_warning rounded-md bg-admin_paper">
              <Typography
                variant="admin_body_dim"
              >
                This ticket has been merged into ticket{" "}
                <span
                  className="text-admin_link hover:underline cursor-pointer"
                  onClick={() => navigateToTicket(mergeInfo.primaryTicket)}
                >
                  #{mergeInfo.primaryTicket}
                </span>
                . Please add any new messages to the primary ticket.
              </Typography>

              <Button
                variant="admin_primary"
                className="mt-3"
                onClick={() => navigateToTicket(mergeInfo.primaryTicket)}
              >
                View Primary Ticket
              </Button>
            </div>
          )}
        </div>

        {/* Right Column - Details & Actions */}
        <div ref={rightColumnRef} className="w-96 flex-shrink-0 flex flex-col gap-4 min-h-0 overflow-y-auto h-full">
          {/* Actions */}
          <div className="bg-admin_paper border border-admin_bordered rounded-md p-4 pb-6 flex-none">
            <Typography variant="admin_h4" className="mb-4">
              Actions
            </Typography>

            {/* Icon buttons row */}
            <div className="flex justify-between mb-6">
              {/* Back button */}
              <Tooltip text="Back to Tickets">
                <Button
                  variant="admin_icon"
                  className="text-admin_text_secondary"
                  onClick={() => {
                    // Navigate directly to tickets page
                    navigate("/admin/customer-support/tickets")
                    onClose && onClose()
                  }}
                  title="Back to Tickets"
                >
                  <ArrowLeftIcon className="w-6 h-6" />
                </Button>
              </Tooltip>

              {/* Tags button */}
              <Tooltip text="Open the Ticket Tag editor">
                <Button
                  variant="admin_icon"
                  className="text-admin_text_secondary"
                  onClick={() =>
                    setTagDialog({
                      isOpen: true,
                      ticketId: ticket.ticketID,
                      ticketLabel: `Ticket #${ticket.ticketID} - ${ticket.summary}`,
                      currentTags: ticket.tags || [],
                    })
                  }
                  title="Tags"
                >
                  <TagIcon className="w-6 h-6" />
                </Button>
              </Tooltip>

              {/* Support Notes button */}
              <Tooltip text="Add note to ticket (Visible to agents only)">
                <Button
                  variant="admin_icon"
                  className="text-admin_text_secondary"
                  onClick={() =>
                    setSupportNoteDialog({
                      isOpen: true,
                      ticketId: ticket.ticketID,
                    })
                  }
                  title="Support Notes"
                >
                  <PencilIcon className="w-6 h-6" />
                </Button>
              </Tooltip>

              {/* Merge Tickets button - only shown for non-merged tickets that aren't part of any merge */}
              {!mergeInfo && !ticket.isMerged && (
                <Tooltip text="Merge with other tickets">
                  <Button
                    variant="admin_icon"
                    className="text-admin_text_secondary"
                    onClick={handleOpenMergeDialog}
                    title="Merge Tickets"
                  >
                    <ArrowPathIcon className="w-6 h-6" />
                  </Button>
                </Tooltip>
              )}

              {/* Spam Toggle button */}
              <Tooltip
                text={ticket.isSpam ? "Mark as Not Spam" : "Mark as Spam"}
              >
                <Button
                  variant="admin_icon"
                  className={`${
                    ticket.isSpam ? "text-admin_error" : "text-admin_text_secondary"
                  }`}
                  onClick={handleToggleSpam}
                  title={ticket.isSpam ? "Mark as Not Spam" : "Mark as Spam"}
                >
                  <ShieldExclamationIcon className="w-6 h-6" />
                </Button>
              </Tooltip>

              {/* Delete button */}
              <Tooltip text="Delete ticket (Can be restored from the Restore dialog)">
                <Button
                  variant="admin_icon"
                  className="text-admin_text_secondary"
                  onClick={handleDeleteTicket}
                  title="Delete Ticket"
                >
                  <TrashIcon className="w-6 h-6" />
                </Button>
              </Tooltip>
            </div>

            {/* Assign To button */}
            <Button
              variant="admin_secondary"
              className="w-full mb-3"
              onClick={() =>
                setAssignDialog({
                  isOpen: true,
                  ticketId: ticket.ticketID,
                  ticketLabel: `${ticket.ticketID} - ${ticket.summary}`,
                  currentAssignee: ticket.assignedTo?._id || null,
                })
              }
            >
              Assign To
            </Button>

            {/* Open/Close Ticket button - Only disabled for secondary tickets */}
            {!mergeInfo?.isSecondary ? (
              ticket.status === "resolved" ? (
                <Button
                  variant="admin_primary"
                  className="w-full"
                  onClick={() => handleStatusChange("open")}
                >
                  Open Ticket
                </Button>
              ) : (
                <Button
                  variant="admin_primary_red"
                  className="w-full"
                  onClick={() => handleStatusChange("resolved")}
                >
                  Resolve Ticket
                </Button>
              )
            ) : (
              <></>
            )}
          </div>

          {/* User Details */}
          {/* <Panel
            variant="admin_bordered"
            title="User Details"
            defaultOpen={false}
            scrollable={true}
            className="min-h-0"
            bodyClassName="overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper"
          >
            <div className="space-y-3 text-admin_text/80">
              {author ? (
                <>
                  {userDetailsConfig.map((detail, index) => (
                    <Typography key={index} variant="admin_body">
                      <span className="font-bold">{detail.label}:</span>{" "}
                      {detail.format
                        ? detail.format(author[detail.key] || detail.fallback)
                        : author[detail.key] || detail.fallback}
                    </Typography>
                  ))}
                </>
              ) : (
                <Typography variant="admin_body">
                  <span className="font-bold">Email:</span>
                  {author?.email || "adrianaf1611@gmail.com"}
                </Typography>
              )}
            </div>

            <Typography variant="admin_h4" className="mt-8 mb-4">
              User Devices
            </Typography>

            <div className="space-y-2 text-admin_text/80">
              {author?.devices &&
              Array.isArray(author.devices) &&
              author.devices.length > 0 ? (
                author.devices.map((device, index) => (
                  <Typography key={index} variant="admin_body">
                    {device}
                  </Typography>
                ))
              ) : (
                <Typography variant="admin_body">
                  Number of devices: 2
                </Typography>
              )}
            </div>
          </Panel> */}

          {/* Merge Information (if applicable) */}
          {mergeInfo && (
            <MergeInfo
              mergeInfo={mergeInfo}
              onNavigateToTicket={navigateToTicket}
            />
          )}
        </div>
      </div>

      {assignDialog.isOpen && (
        <AssignDialog
          isOpen={assignDialog.isOpen}
          ticketId={assignDialog.ticketId}
          ticketLabel={assignDialog.ticketLabel}
          currentAssignee={assignDialog.currentAssignee}
          onClose={() =>
            setAssignDialog({
              isOpen: false,
              ticketId: null,
              ticketLabel: "",
              currentAssignee: null,
            })
          }
          onAssigned={async () => {
            // First close the dialog immediately
            setAssignDialog({
              isOpen: false,
              ticketId: null,
              ticketLabel: "",
              currentAssignee: null,
            })

            // Then load the data in the background without showing loading indicator
            await loadTicketDetails(false)
            await refreshStatistics()
          }}
        />
      )}

      {tagDialog.isOpen && (
        <TagDialog
          isOpen={tagDialog.isOpen}
          ticketId={tagDialog.ticketId}
          ticketLabel={tagDialog.ticketLabel}
          currentTags={tagDialog.currentTags}
          ticket={ticket} // Added ticket object
          onClose={() =>
            setTagDialog({
              isOpen: false,
              ticketId: null,
              ticketLabel: "",
              currentTags: [],
            })
          }
          onUpdated={async () => {
            // Load ticket details without showing loading indicator
            await loadTicketDetails(false)
            await refreshStatistics()
            setTagDialog({
              isOpen: false,
              ticketId: null,
              ticketLabel: "",
              currentTags: [],
            })
          }}
        />
      )}

      {supportNoteDialog.isOpen && (
        <SupportNoteDialog
          isOpen={supportNoteDialog.isOpen}
          ticketId={supportNoteDialog.ticketId}
          onClose={() =>
            setSupportNoteDialog({
              isOpen: false,
              ticketId: null,
            })
          }
          onNoteAdded={async () => {
            // Load ticket details without showing loading indicator
            await loadTicketDetails(false)
            await refreshStatistics()
            setSupportNoteDialog({
              isOpen: false,
              ticketId: null,
            })
          }}
        />
      )}

      {mergeDialog.isOpen && (
        <MergeTickets
          isOpen={mergeDialog.isOpen}
          initialTicketId={mergeDialog.ticketId}
          initialTicketLabel={mergeDialog.ticketLabel}
          onClose={async (merged) => {
            setMergeDialog({
              isOpen: false,
              ticketId: null,
              ticketLabel: "",
            })

            // If merged successfully, refresh the ticket details without showing loading indicator
            if (merged) {
              await loadTicketDetails(false)
              await refreshStatistics()
            }
          }}
        />
      )}
    </div>
  )
}
